import { installAIChatPresence } from '@/app/ai/multiplayer/aiChatPresence';
import { aiAnalystOfflineChats } from '@/app/ai/offline/aiAnalystChats';
import { aiAnalystServerChats } from '@/app/ai/offline/aiAnalystServerChats';
import { mergeChatLists } from '@/app/ai/utils/mergeChatLists';
import { events } from '@/app/events/events';
import type { Chat } from 'quadratic-shared/typesAndSchemasAI';
import { currentChatAtom, currentChatNameAtom } from './chatAtoms';
import {
  chatsWithPersistenceAtom,
  loadingWithPersistenceAtom,
  showAIAnalystWithEffectsAtom,
  showChatHistoryWithEffectsAtom,
} from './effectAtoms';
import {
  abortControllerAtom,
  aiAnalystInitializedAtom,
  chatsAtom,
  currentChatBaseAtom,
  emptyChatSuggestionsAtom,
  loadingAtom,
  showAIAnalystAtom,
  showChatHistoryAtom,
} from './primitives';
import { readCurrentFileAIState, writeCurrentFileAIState } from './sessionState';
import { aiStore } from './store';
import { createDefaultChat } from './types';

// ============================================================================
// AI Analyst Visibility Actions
// ============================================================================

/**
 * Show or hide the AI Analyst panel.
 * Handles cleanup (abort, focus grid) when hiding.
 *
 * Delegates to showAIAnalystWithEffectsAtom for the actual logic.
 */
export function setShowAIAnalyst(show: boolean): void {
  aiStore.set(showAIAnalystWithEffectsAtom, show);
}

/**
 * Toggle the AI Analyst visibility.
 */
export function toggleShowAIAnalyst(): void {
  setShowAIAnalyst(!aiStore.get(showAIAnalystAtom));
}

/**
 * Get whether the AI Analyst is currently shown.
 */
export function getShowAIAnalyst(): boolean {
  return aiStore.get(showAIAnalystAtom);
}

// ============================================================================
// Chat History Actions
// ============================================================================

/**
 * Show or hide the chat history view.
 * Aborts any pending request when showing history.
 *
 * Delegates to showChatHistoryWithEffectsAtom for the actual logic.
 */
export function setShowChatHistory(show: boolean): void {
  aiStore.set(showChatHistoryWithEffectsAtom, show);
}

/**
 * Whether a chat thread is available to open in this file (it may belong to a
 * file the user has since left, or predate what the backend still keeps).
 */
export function hasChat(chatId: string): boolean {
  return aiStore.get(chatsAtom).some((chat) => chat.id === chatId);
}

/**
 * Open a saved chat thread in the AI Analyst, e.g. from a cell's edit history
 * pointing at the conversation that wrote the value. Returns false when the
 * thread is no longer available, so callers can avoid offering a dead link.
 */
export function openChat(chatId: string): boolean {
  const chat = aiStore.get(chatsAtom).find((candidate) => candidate.id === chatId);
  if (!chat) return false;

  // currentChatAtom closes the history view and syncs the chats list, so this is
  // the same path the history list itself uses to switch threads.
  aiStore.set(currentChatAtom, chat);
  setShowAIAnalyst(true);
  return true;
}

// ============================================================================
// Chat Persistence Actions
// ============================================================================

/**
 * Save an AI-generated or user-edited name for a specific chat with persistence.
 */
export function saveChatNameForChat(chatId: string, newName: string): void {
  const trimmedName = newName.trim();
  if (!trimmedName || !chatId) {
    return;
  }

  const chats = aiStore.get(chatsAtom);
  const existingChat = chats.find((chat) => chat.id === chatId);
  if (!existingChat) {
    return;
  }

  const updatedChat: Chat = { ...existingChat, name: trimmedName };
  // chatsWithPersistenceAtom's setter syncs currentChatBaseAtom when the current
  // chat is part of the new list, so no explicit currentChatBaseAtom update is needed here.
  aiStore.set(chatsWithPersistenceAtom, [...chats.filter((chat) => chat.id !== chatId), updatedChat]);
}

/**
 * Save a chat name for the current chat with persistence.
 */
export function saveChatName(newName: string): void {
  const trimmedName = newName.trim();
  if (!trimmedName) {
    return;
  }

  const currentChat = aiStore.get(currentChatBaseAtom);
  if (currentChat.id) {
    saveChatNameForChat(currentChat.id, trimmedName);
    return;
  }

  aiStore.set(currentChatNameAtom, trimmedName);
  const updatedChat = aiStore.get(currentChatBaseAtom);
  if (updatedChat.id) {
    aiStore.set(chatsWithPersistenceAtom, [
      ...aiStore.get(chatsAtom).filter((chat) => chat.id !== updatedChat.id),
      updatedChat,
    ]);
  }
}

/**
 * Update chats list with persistence.
 * Handles saving new/changed chats and deleting removed chats.
 *
 * Delegates to chatsWithPersistenceAtom for the actual logic.
 * Note: The effect atom handles persistence asynchronously with .catch(),
 * so this function no longer needs to be async.
 */
export function updateChatsWithPersistence(newChats: Chat[]): void {
  aiStore.set(chatsWithPersistenceAtom, newChats);
}

/**
 * Set loading state with persistence on completion.
 * When loading transitions from true to false, saves the current chat.
 *
 * Delegates to loadingWithPersistenceAtom for the actual logic.
 * Note: The effect atom handles persistence asynchronously with .catch(),
 * so this function no longer needs to be async.
 */
export function setLoadingWithPersistence(newLoading: boolean): void {
  aiStore.set(loadingWithPersistenceAtom, newLoading);
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Check if the AI Analyst has been initialized.
 */
export function getAIAnalystInitialized(): boolean {
  return aiStore.get(aiAnalystInitializedAtom);
}

/**
 * Reset the AI Analyst initialization state.
 * Useful when switching files or logging out.
 * Aborts all in-flight AI requests to prevent stale data issues.
 */
export function resetAIAnalystInitialized(): void {
  aiStore.set(aiAnalystInitializedAtom, false);

  // Abort main AI request if in progress
  const mainAbortController = aiStore.get(abortControllerAtom);
  mainAbortController?.abort();
  aiStore.set(abortControllerAtom, undefined);
  aiStore.set(loadingAtom, false);

  // Abort any in-flight empty chat suggestions request and reset state
  const currentSuggestions = aiStore.get(emptyChatSuggestionsAtom);
  currentSuggestions.abortController?.abort();
  aiStore.set(emptyChatSuggestionsAtom, {
    suggestions: undefined,
    contextHash: undefined,
    loading: false,
    abortController: undefined,
  });
}

// The file uuid currently loaded, used to key the per-tab session record that
// the persistence subscriptions write to.
let currentFileUuid: string | undefined;

// Guards installPersistenceSubscriptions so the subscriptions are only wired up
// once for the lifetime of the tab (they read currentFileUuid, which is updated
// per file, so a single set of subscriptions covers every file).
let persistenceSubscriptionsInstalled = false;

/**
 * Persist the current AI Analyst open/closed state and open chat thread to the
 * per-tab session record so a refresh in this tab can restore it.
 */
function persistCurrentFileAIState(): void {
  if (!currentFileUuid) return;
  writeCurrentFileAIState({
    fileUuid: currentFileUuid,
    aiAnalystOpen: aiStore.get(showAIAnalystAtom),
    currentChatId: aiStore.get(currentChatBaseAtom).id,
  });
}

/**
 * Wire up subscriptions that persist the AI Analyst state whenever the panel
 * visibility or the current chat thread changes. All current-chat writers
 * funnel through currentChatBaseAtom and visibility through showAIAnalystAtom,
 * so these two subscriptions cover every code path.
 */
function installPersistenceSubscriptions(): void {
  if (persistenceSubscriptionsInstalled) return;
  persistenceSubscriptionsInstalled = true;
  aiStore.sub(showAIAnalystAtom, persistCurrentFileAIState);
  aiStore.sub(currentChatBaseAtom, persistCurrentFileAIState);
  installAIChatPresence();
}

/**
 * Initialize the AI Analyst with user and file context.
 * Loads saved chats from offline storage.
 *
 * Restores the panel open/closed state and open chat thread from the per-tab
 * session record when it matches the current file; otherwise falls back to the
 * user's "Show AI on startup" preference with a blank chat.
 */
export async function initializeAIAnalyst(userEmail: string, fileUuid: string, showOnStartup: boolean): Promise<void> {
  currentFileUuid = fileUuid;

  const sessionState = readCurrentFileAIState();
  const matchesFile = sessionState?.fileUuid === fileUuid;

  aiStore.set(showAIAnalystAtom, matchesFile ? sessionState.aiAnalystOpen : showOnStartup);

  try {
    // The backend is the source of truth for chat history (shared across all
    // collaborators on the file); IndexedDB is a local cache, used both as a
    // fallback when the backend is unreachable and as the source for the thread
    // this tab had open. A transcript reaches the backend only once a turn
    // completes, so a successful response can still omit that thread — treating
    // "reachable" as "complete" reopens the panel on a blank chat after a
    // reload. mergeChatLists layers it back on under the same policy the
    // presence-driven live refresh uses.
    aiAnalystServerChats.init(fileUuid);
    await aiAnalystOfflineChats.init(userEmail, fileUuid);
    const localChats = await aiAnalystOfflineChats.loadChats().catch(() => [] as Chat[]);
    const restoredChatId = matchesFile ? sessionState.currentChatId : undefined;
    const cachedOpenChat = restoredChatId ? localChats.find((chat) => chat.id === restoredChatId) : undefined;
    let chats: Chat[];
    try {
      chats = mergeChatLists(await aiAnalystServerChats.loadChats(), cachedOpenChat);
    } catch {
      chats = localChats;
    }
    aiStore.set(chatsAtom, chats);

    // Restore the open chat thread when the session record matches this file
    // and the thread still exists; otherwise start with a blank chat. This
    // also prevents stale chat data from a previous file being displayed.
    const restoredChat = restoredChatId ? chats.find((chat) => chat.id === restoredChatId) : undefined;
    aiStore.set(currentChatBaseAtom, restoredChat ?? createDefaultChat());
    aiStore.set(showChatHistoryAtom, false);
  } catch (error) {
    console.error('[AIAnalystOfflineChats]: ', error);
  } finally {
    installPersistenceSubscriptions();
    persistCurrentFileAIState();
    aiStore.set(aiAnalystInitializedAtom, true);
    events.emit('aiAnalystInitialized');
  }
}
